import {
  Button,
  FormInput,
  FormNumber,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { SparklesIcon } from "lucide-react";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import type { z } from "zod";
import { CreatePrivateLessonTemplateSchema } from "@/modules/private-lessons/domain/contracts";
import { isDevMode } from "@/shared/lib/config";
import { PrivateLessonLocationKindRadio } from "./PrivateLessonLocationKindRadio";
import { PrivateLessonPackageKindRadio } from "./PrivateLessonPackageKindRadio";
import { PrivateLessonTemplateTiersEditor } from "./PrivateLessonTemplateTiersEditor";

const PrivateLessonTemplateFormSchema = CreatePrivateLessonTemplateSchema;
export type PrivateLessonTemplateFormValues = z.infer<
  typeof PrivateLessonTemplateFormSchema
>;

type PrivateLessonTemplateFormProps = {
  initialValues?: Partial<PrivateLessonTemplateFormValues>;
  onSubmit: (values: PrivateLessonTemplateFormValues) => void;
  submitting?: boolean;
};

const SAMPLE_PRIVATE_SHARED_TIERS = [
  { minPeople: 1, maxPeople: 2, pricePerHour: 380_000, extraPersonPrice: null },
  { minPeople: 3, maxPeople: 4, pricePerHour: 750_000, extraPersonPrice: null },
  {
    minPeople: 5,
    maxPeople: 10,
    pricePerHour: 1_200_000,
    extraPersonPrice: 100_000,
  },
];

export function PrivateLessonTemplateForm({
  initialValues,
  onSubmit,
  submitting,
}: PrivateLessonTemplateFormProps) {
  const methods = RHF.useForm<PrivateLessonTemplateFormValues>({
    // @ts-expect-error zod default() typing with zodResolver
    resolver: zodResolver(PrivateLessonTemplateFormSchema),
    defaultValues: {
      name: initialValues?.name || "",
      packageKind: initialValues?.packageKind ?? "individual",
      totalHours: initialValues?.totalHours ?? 1,
      validDays: initialValues?.validDays ?? undefined,
      pricePerHour: initialValues?.pricePerHour ?? undefined,
      groupSizeMin: initialValues?.groupSizeMin ?? null,
      groupSizeMax: initialValues?.groupSizeMax ?? null,
      flatPrice: initialValues?.flatPrice ?? null,
      tiers: initialValues?.tiers ?? [],
      defaultLocationKind: initialValues?.defaultLocationKind ?? "studio",
      description: initialValues?.description || "",
      status: initialValues?.status || "active",
    },
  });

  const packageKind = useWatch({
    control: methods.control,
    name: "packageKind",
  });
  const pricePerHour = useWatch({
    control: methods.control,
    name: "pricePerHour",
  });
  const flatPrice = useWatch({
    control: methods.control,
    name: "flatPrice",
  });
  const isGroup = packageKind === "group";
  const isPrivateShared = packageKind === "private_shared";

  useEffect(() => {
    if (isGroup) {
      methods.setValue("pricePerHour", 0);
      methods.setValue("tiers", []);
      if ((methods.getValues("totalHours") ?? 1) === 1) {
        methods.setValue("totalHours", 10);
      }
    } else if (isPrivateShared) {
      methods.setValue("totalHours", 1);
      methods.setValue("pricePerHour", 0);
      methods.setValue("groupSizeMin", null);
      methods.setValue("groupSizeMax", null);
      methods.setValue("flatPrice", null);
      if ((methods.getValues("tiers") ?? []).length === 0) {
        methods.setValue("tiers", SAMPLE_PRIVATE_SHARED_TIERS);
      }
    } else {
      methods.setValue("totalHours", 1);
      methods.setValue("groupSizeMin", null);
      methods.setValue("groupSizeMax", null);
      methods.setValue("flatPrice", null);
      methods.setValue("tiers", []);
    }
  }, [isGroup, isPrivateShared, methods]);

  const handleAutoFill = () => {
    if (isPrivateShared) {
      methods.reset({
        name: "PRIVATE PACKAGE (tier ຕາມຈຳນວນຄົນ)",
        packageKind: "private_shared",
        totalHours: 1,
        validDays: 90,
        pricePerHour: 0,
        groupSizeMin: null,
        groupSizeMax: null,
        flatPrice: null,
        tiers: SAMPLE_PRIVATE_SHARED_TIERS,
        defaultLocationKind: "studio",
        description: "",
        status: "active",
      });
    } else if (isGroup) {
      methods.reset({
        name: "ແພັກເກດກຸ່ມ 5–7 ຄົນ (10 ຊົ່ວໂມງ)",
        packageKind: "group",
        totalHours: 10,
        validDays: 90,
        pricePerHour: 0,
        groupSizeMin: 5,
        groupSizeMax: 7,
        flatPrice: 1_200_000,
        tiers: [],
        defaultLocationKind: "studio",
        description: "",
        status: "active",
      });
    } else {
      methods.reset({
        name: "ແພັກເກດຮຽນສ່ວນຕົວ (ອັດຕາຕໍ່ຊົ່ວໂມງ)",
        packageKind: "individual",
        totalHours: 1,
        validDays: 90,
        pricePerHour: 50000,
        groupSizeMin: null,
        groupSizeMax: null,
        flatPrice: null,
        tiers: [],
        defaultLocationKind: "studio",
        description: "",
        status: "active",
      });
    }
  };

  const handleFormSubmit = (values: PrivateLessonTemplateFormValues) => {
    if (values.packageKind === "individual") {
      onSubmit({
        ...values,
        totalHours: 1,
        groupSizeMin: null,
        groupSizeMax: null,
        flatPrice: null,
        tiers: [],
      });
      return;
    }
    if (values.packageKind === "private_shared") {
      onSubmit({
        ...values,
        totalHours: 1,
        pricePerHour: 0,
        groupSizeMin: null,
        groupSizeMax: null,
        flatPrice: null,
      });
      return;
    }
    onSubmit({ ...values, tiers: [] });
  };

  return (
    <FormRoot
      methods={methods}
      onSubmit={handleFormSubmit}
      className="space-y-6"
    >
      <div className="space-y-4">
        <FormInput
          name="name"
          label="ຊື່ແພັກເກດ"
          placeholder="ປ້ອນຊື່ແພັກເກດ"
          requiredMark
        />

        <RHF.Controller
          name="packageKind"
          control={methods.control}
          render={({ field }) => (
            <PrivateLessonPackageKindRadio
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              name={field.name}
            />
          )}
        />

        {isPrivateShared ? (
          <>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
              Private Package —{" "}
              <span className="font-medium text-foreground">
                tier ລາຄາ/ຊົ່ວໂມງຕາມຈຳນວນຄົນ · ເລືອກຊົ່ວໂມງຕອນຂາຍ
              </span>
            </div>
            <RHF.Controller
              name="defaultLocationKind"
              control={methods.control}
              render={({ field }) => (
                <PrivateLessonLocationKindRadio
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
            <PrivateLessonTemplateTiersEditor />
          </>
        ) : isGroup ? (
          <>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
              ແມ່ແບບກຸ່ມກຳນົດ{" "}
              <span className="font-medium text-foreground">
                ຊົ່ວໂມງຄົງທີ່ + ລາຄາຕໍ່ຄົນ (ຄູນຕາມຈຳນວນສະມາຊິກຕອນຂາຍ)
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <FormNumber
                name="totalHours"
                label="ຊົ່ວໂມງຕໍ່ກຸ່ມ"
                placeholder="ເຊັ່ນ 10"
                requiredMark
              />
              <FormNumber
                name="groupSizeMin"
                label="ຈຳນວນຄົນຂັ້ນຕ່ຳ"
                placeholder="ເຊັ່ນ 5"
                requiredMark
              />
              <FormNumber
                name="groupSizeMax"
                label="ຈຳນວນຄົນສູງສຸດ"
                placeholder="ເຊັ່ນ 7"
                requiredMark
              />
            </div>

            <FormPrice
              name="flatPrice"
              label="ລາຄາຕໍ່ຄົນ (ກີບ)"
              placeholder="ປ້ອນລາຄາຕໍ່ຄົນ"
              requiredMark
              currencySymbol="₭"
            />

            {flatPrice != null && flatPrice > 0 && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">ລາຄາຕໍ່ຄົນ:</span>
                  <span className="font-semibold text-lg">
                    {flatPrice.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-muted-foreground text-sm">
              ແມ່ແບບກຳນົດເປັນ{" "}
              <span className="font-medium text-foreground">
                1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ
              </span>
              . ຈຳນວນຊົ່ວໂມງທີ່ຂາຍໃຫ້ນັກຮຽນເລືອກຕອນຂາຍແພັກເກດ.
            </div>

            <FormPrice
              name="pricePerHour"
              label="ລາຄາຕໍ່ຊົ່ວໂມງ (ກີບ)"
              placeholder="ປ້ອນລາຄາຕໍ່ຊົ່ວໂມງ"
              requiredMark
              currencySymbol="₭"
            />

            {pricePerHour != null && pricePerHour > 0 && (
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">ອັດຕາຕໍ່ 1 ຊົ່ວໂມງ:</span>
                  <span className="font-semibold text-lg">
                    {pricePerHour.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        <FormNumber
          name="validDays"
          label="ຈຳນວນວັນ (ເລີ່ມຕົ້ນ)"
          placeholder="ປ້ອນຈຳນວນວັນ"
          requiredMark
        />

        {!isPrivateShared && (
          <RHF.Controller
            name="defaultLocationKind"
            control={methods.control}
            render={({ field }) => (
              <PrivateLessonLocationKindRadio
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />
        )}

        <FormTextarea
          name="description"
          label="ຄຳອະທິບາຍ"
          placeholder="ປ້ອນຄຳອະທິບາຍ (ບໍ່ບັງຄັບ)"
          rows={3}
        />

        <FormSelect
          label="ສະຖານະ"
          name="status"
          options={[
            { value: "active", label: "ໃຊ້ງານ" },
            { value: "inactive", label: "ບໍ່ໃຊ້ງານ" },
          ]}
        />
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        {isDevMode && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAutoFill}
            disabled={submitting}
          >
            <SparklesIcon className="size-4" />
            Auto Fill
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => methods.reset()}
          disabled={submitting}
        >
          ລ້າງ
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
        </Button>
      </div>
    </FormRoot>
  );
}
